import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  Box,
  Divider,
  alpha,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "#FEF3C7", color: "#92400E" },
  in_progress: { label: "In Progress", bg: "#DBEAFE", color: "#1E40AF" },
  completed: { label: "Completed", bg: "#D1FAE5", color: "#065F46" },
  cancelled: { label: "Cancelled", bg: "#F3F4F6", color: "#6B7280" },
};

const PRIORITY_CONFIG = {
  high: { label: "High", bg: "#FEE2E2", color: "#991B1B", icon: "!!!" },
  medium: { label: "Medium", bg: "#FEF3C7", color: "#92400E", icon: "!!" },
  low: { label: "Low", bg: "#E0E7FF", color: "#3730A3", icon: "!" },
};

function InfoRow({ icon, label, value, chip }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}
      </Typography>
      {chip || (
        <Typography variant="body2" fontWeight={500}>
          {value || "—"}
        </Typography>
      )}
    </Stack>
  );
}

export default function TaskDetailDialog({ task, open, onClose }) {
  if (!task) return null;

  const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const priorityCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      {/* Header con gradiente */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
          px: 3,
          py: 2.5,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: "white", flex: 1, mr: 2 }}
          >
            {task.title}
          </Typography>
          <Stack direction="row" spacing={0.75}>
            <Chip
              label={statusCfg.label}
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.9),
                color: statusCfg.color,
              }}
            />
            <Chip
              label={priorityCfg.label}
              size="small"
              sx={{
                height: 26,
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.9),
                color: priorityCfg.color,
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          {/* Descripción */}
          {task.description && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {task.description}
              </Typography>
              <Divider />
            </>
          )}

          {/* Detalles */}
          <Stack spacing={1.5}>
            <InfoRow
              icon={<EventIcon fontSize="small" />}
              label="Due Date"
              value={task.dueDate || "No date set"}
            />
            <InfoRow
              icon={<AssignmentIndIcon fontSize="small" />}
              label="Assignee"
              value={task.assigneeName}
            />
            <InfoRow
              icon={<PersonIcon fontSize="small" />}
              label="Created By"
              value={task.createdBy || task.creatorName}
            />
            <InfoRow
              icon={<CategoryIcon fontSize="small" />}
              label="Category"
              value={task.category}
            />
            <InfoRow
              icon={<FlagIcon fontSize="small" />}
              label="Priority"
              chip={
                <Chip
                  label={priorityCfg.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: priorityCfg.bg,
                    color: priorityCfg.color,
                  }}
                />
              }
            />
            {task.patientName && (
              <InfoRow
                icon={<LocalHospitalIcon fontSize="small" />}
                label="Patient"
                value={task.patientName}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
